import { EM_DASH } from "constants/index";

import { VFC } from "react";

import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import Text from "components/Text";
import capitalize from "lodash/capitalize";
import { useTranslation } from "react-i18next";

import { CargoGroup } from "../api/types";
import { getCargoGroupEquipmentString } from "../utils/getCargoGroupEquipmentString";
import { getHandlingUnitString } from "../utils/getHandlingUnitString";
import { mapPalletSizeStringToPalletType } from "../utils/mapPalletSizeStringToPalletType";

interface CargoGroupsOverviewViewProps {
  cargoGroups: CargoGroup[];
  variant?: "container" | "handlingUnit";
}

const CargoGroupsOverviewView: VFC<CargoGroupsOverviewViewProps> = (props) => {
  const { cargoGroups, variant = "container" } = props;
  const { t } = useTranslation(["common", "shipper", "dispatchRequest"]);

  if (variant === "container") {
    return (
      <div className="w-full grid grid-cols-4">
        <div>
          <Text size="medium" weight="bold" typographyProps={{ style: { textTransform: "uppercase" } }}>
            {cargoGroups.some((cargoGroup) => cargoGroup.packaging_type === ShipmentPackagingType.Containers)
              ? t("common:containers")
              : t("common:equipment")}
          </Text>
        </div>
        <div>
          <Text size="medium" weight="bold" typographyProps={{ style: { textTransform: "uppercase" } }}>
            {t("common:weight")}
          </Text>
        </div>
        <div>
          <Text size="medium" weight="bold" typographyProps={{ style: { textTransform: "uppercase" } }}>
            {t("common:volume")}
          </Text>
        </div>
        <div>
          <Text size="medium" weight="bold" typographyProps={{ style: { textTransform: "uppercase" } }}>
            {t("common:commodities")}
          </Text>
        </div>

        {cargoGroups.map((cargoGroup) => {
          const containerString = [getCargoGroupEquipmentString(cargoGroup, t)];

          return (
            <>
              <div>
                <Text size="medium" weight="bold">
                  {containerString}
                </Text>
              </div>
              <div>
                <Text size="medium">
                  {cargoGroup.weight_per_count
                    ? t("dispatchRequest:totalWeight", { context: "KG", value: cargoGroup.weight_per_count })
                    : EM_DASH}
                </Text>
              </div>
              <div>
                {cargoGroup.volume_per_count ? t("common:volume_cbm", { value: cargoGroup.volume_per_count }) : EM_DASH}
              </div>
              <div>{cargoGroup.commodities && cargoGroup.commodities.length ? cargoGroup.commodities : EM_DASH}</div>
              {cargoGroup.handling_units.map((handlingUnit) => (
                <div className="col-span-4 pl-6">
                  <Text size="medium">{getHandlingUnitString(handlingUnit, t)}</Text>
                </div>
              ))}
            </>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-full grid grid-cols-6">
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          {t("common:item")}
        </Text>
      </div>
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          {t("shipper:dimensions")}
        </Text>
      </div>
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          {t("common:totalWeight")}
        </Text>
      </div>
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          Stackable
        </Text>
      </div>
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          Packing Unit
        </Text>
      </div>
      <div className="p-2 text-left">
        <Text size="small" typographyProps={{ color: "textSecondary" }}>
          {t("common:caseCount")}
        </Text>
      </div>
      {cargoGroups.map((handlingUnit) => {
        const packingUnit = handlingUnit.packing_units[0];

        const isPalletized = handlingUnit.packaging_type.includes("PALLETS");
        const count = handlingUnit.count;
        const lengthPerCount = handlingUnit.length_per_count;
        const widthPerCount = handlingUnit.width_per_count;
        const palletType = mapPalletSizeStringToPalletType[`${lengthPerCount}_${widthPerCount}`];

        const itemDescription = isPalletized
          ? `${!!count ? `${count} x ` : ""}${
              palletType
                ? `${t(`shipper:packingTypeMap.${handlingUnit.packaging_type}`)} (${t(
                    `shipper:palletTypeMap.${palletType}`
                  )})`
                : t("shipper:palletized")
            }`
          : `${!!handlingUnit.count ? `${handlingUnit.count} x ` : ""}${
              handlingUnit.packaging_type
                ? capitalize(handlingUnit.packaging_type).replaceAll(/_/g, " ")
                : t("shipper:floorLoaded")
            }`;

        const packingUnitDescription =
          isPalletized && packingUnit
            ? `${!!packingUnit.count ? `${packingUnit.count} x ` : ""}${t(
                `shipper:packingTypeMap.${packingUnit.packaging_type}`
              )}`
            : EM_DASH;

        return (
          <>
            <div className="p-2">
              <Text size="small" weight="bold">
                {itemDescription}
              </Text>
            </div>
            <div className="p-2">
              <Text size="small" weight="bold">
                {handlingUnit.length_per_count && handlingUnit.width_per_count
                  ? `${handlingUnit.length_per_count} x ${handlingUnit.width_per_count}${
                      handlingUnit.height_per_count ? ` x ${handlingUnit.height_per_count}` : ""
                    } ${t("common:in")}`
                  : EM_DASH}
              </Text>
            </div>
            <div className="p-2">
              <Text size="small" weight="bold">
                {handlingUnit.weight_per_count
                  ? // @ts-expect-error this typing is in fact fine
                    t(`common:{{value}}${(handlingUnit.weight_unit ?? "LB").toLowerCase()}`, {
                      value: (handlingUnit.weight_per_count ?? 1) * (handlingUnit.count ?? 1),
                    })
                  : EM_DASH}
              </Text>
            </div>
            <div className="p-2">
              <Text size="small" weight="bold">
                {isPalletized ? (handlingUnit.is_stackable ? t("common:yes") : t("common:no")) : EM_DASH}
              </Text>
            </div>
            <div className="p-2">
              <Text size="small" weight="bold">
                {packingUnitDescription ?? EM_DASH}
              </Text>
            </div>
            <div className="p-2">
              <Text size="small" weight="bold">
                {handlingUnit.case_count ?? packingUnit?.case_count ?? EM_DASH}
              </Text>
            </div>
          </>
        );
      })}
    </div>
  );
};

export default CargoGroupsOverviewView;
